import { loadPages, PAGE_SIZE, type Page, pageUrl } from '../pages.ts';
import { ApiError, InputError } from './errors.ts';
import { type Money } from './format.ts';
import { me, runsCashBox } from './session.ts';

/**
 * The console's requests to the API: every call carries the session JWT (Stytch or the identity
 * simulator), writes carry an idempotency key, and errors come back as ApiError.
 */

/** Where the session's JWT comes from (Stytch or the simulator); main.ts sets it at boot. */
let sessionSource: () => string | null | undefined = () => null;
export function setSessionSource(source: () => string | null | undefined) {
  sessionSource = source;
}
/** The session's JWT, for a request that sends a raw file with headers of its own. */
export const sessionJwt = () => sessionSource();

export async function api<T>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  path: string,
  body?: unknown,
  /** A write that follows a request in course sends its key (retry-keys.ts); else a new one. */
  opts: { idempotencyKey?: string } = {},
): Promise<T> {
  const jwt = sessionJwt();
  const res = await fetch(path, {
    method,
    headers: {
      ...(jwt ? { authorization: `Bearer ${jwt}` } : {}),
      ...(body === undefined ? {} : { 'content-type': 'application/json' }),
      ...(method !== 'GET'
        ? { 'idempotency-key': opts.idempotencyKey ?? crypto.randomUUID() }
        : {}),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => ({}));
  if (!res.ok)
    throw new ApiError(
      res.status,
      data.code ?? String(res.status),
      data.message ?? res.statusText,
      (data.code === 'INVALID_ANSWERS' || data.code === 'APPLICATION_INVALID') &&
        Array.isArray(data.errors)
        ? data.errors
        : [],
    );
  return data as T;
}

/**
 * How many pages of each list the person loaded with «Cargar más»; back to one when they open
 * the view from the menu or change its filter or search.
 */
export let listPages: Record<string, number> = {};
/** «Cargar más» starts again: every list back to its first page. */
export function resetListPages() {
  listPages = {};
}
/** The pages of a list loaded so far (the API answers at most 100 per page). */
export function paged<T>(list: string, path: string, size = PAGE_SIZE) {
  return loadPages(
    (cursor) => api<Page<T>>('GET', pageUrl(path, size, cursor)),
    listPages[list] ?? 1,
  );
}

/** Each person who handles cash has one box, reopened every day: "caja-" + their e-mail name. */
export const myBoxId = () =>
  `caja-${(me?.email.split('@')[0] ?? 'x').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
export type Box = { id: string; state: string; expected: Money | null; difference?: Money | null };
/**
 * The person's box, or null while it was never opened (CLOSED). Read from the agency's list of
 * boxes, which whoever keeps a box may read: asking for a box that does not exist yet answers 404,
 * an error in the browser's console for what is only a closed box (#242).
 */
export const myBox = async (): Promise<Box | null> => {
  if (!runsCashBox()) return null;
  const id = myBoxId();
  return (await api<Box[]>('GET', '/v1/cash-boxes')).find((b) => b.id === id) ?? null;
};

export const bytesText = (n: number) =>
  n < 1024 * 1024
    ? `${Math.max(1, Math.round(n / 1024))} KB`
    : `${(n / 1024 / 1024).toFixed(1)} MB`;

/** The raw file, with its name; the API decides what it really is. */
export async function sendFile<T = unknown>(path: string, file: File): Promise<T> {
  if (file.size > 5 * 1024 * 1024)
    throw new InputError(`«${file.name}» pesa ${bytesText(file.size)}; el máximo es 5 MB.`);
  const jwt = sessionJwt();
  const res = await fetch(path, {
    method: 'POST',
    headers: {
      ...(jwt ? { authorization: `Bearer ${jwt}` } : {}),
      'content-type': file.type || 'application/octet-stream',
      'x-file-name': encodeURIComponent(file.name),
      ...(file.lastModified ? { 'x-taken-at': new Date(file.lastModified).toISOString() } : {}),
      'idempotency-key': crypto.randomUUID(),
    },
    body: file,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, data.code ?? String(res.status), data.message ?? '');
  return data as T;
}

/** Saves what the API answers as an attachment (its name from Content-Disposition), uncached. */
export async function download(path: string, fallbackName = 'documento') {
  const jwt = sessionJwt();
  const res = await fetch(path, {
    headers: jwt ? { authorization: `Bearer ${jwt}` } : {},
    cache: 'no-store',
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new ApiError(res.status, data.code ?? String(res.status), data.message ?? '');
  }
  const name =
    decodeURIComponent(
      /filename\*=UTF-8''([^;]+)/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? '',
    ) || fallbackName;
  const url = URL.createObjectURL(await res.blob());
  const a = Object.assign(document.createElement('a'), { href: url, download: name });
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
  return name;
}

/** The chosen file as base64 (without the data: prefix). */
export const fileBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).replace(/^data:[^,]*,/, ''));
    reader.onerror = () => reject(new InputError('No se pudo leer el archivo.'));
    reader.readAsDataURL(file);
  });
